import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  zIndex,
} from '../styles/designTokens';

// 캘린더 컨테이너 스타일
const CalendarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  flex-wrap: wrap;
  row-gap: ${spacing[1]};
  background-color: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  padding: 0 ${spacing[1]};
  min-width: 0;
`;

// 날짜 입력 필드 스타일
const DateInput = styled.input<{ $weekMode?: boolean }>`
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  border-radius: 0;
  font-size: ${typography.fontSize.sm};
  background-color: transparent;
  cursor: pointer;
  /* 주간 모드: yyyy.mm.dd ~ yyyy.mm.dd 전체가 잘리지 않도록 최소 폭 확보 */
  min-width: ${props => (props.$weekMode ? '320px' : '120px')};
  text-align: center;
  flex-grow: 1;
  flex-shrink: 0;
  font-family: ${typography.fontFamily.sans};
  color: ${colors.semantic.text.primary};
  
  &:focus {
    outline: none;
  }

  /* 직접 입력 모드일 때 커서 스타일 변경 */
  &.editing {
    cursor: text;
  }
`;

// 아이콘 버튼 스타일
const IconButton = styled.button`
  background: none;
  border: none;
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  padding: ${spacing[2]};
  color: ${colors.semantic.text.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${colors.semantic.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
    border-radius: ${borderRadius.sm};
  }
`;

const TodayButton = styled(IconButton)`
  padding: ${spacing[1]} ${spacing[2]};
`;

// 캘린더 팝업 스타일
const CalendarPopup = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: ${zIndex.datePickerPopup};
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
  padding: ${spacing[4]};
  min-width: 280px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: ${spacing[1]};
`;

// 캘린더 헤더 스타일
const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing[4]};
`;

// 월/년 표시 스타일
const MonthYear = styled.h3`
  margin: 0;
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.primary};
`;

// 네비게이션 버튼 스타일
const NavButton = styled.button`
  background: none;
  border: none;
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.sm};
  color: ${colors.semantic.text.tertiary};
  
  &:hover {
    background-color: ${colors.semantic.background.secondary};
    color: ${colors.semantic.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;

// 요일 헤더 스타일
const WeekdaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${spacing[1]};
  margin-bottom: ${spacing[2]};
`;

// 요일 셀 스타일
const WeekdayCell = styled.div`
  text-align: center;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.semantic.text.tertiary};
  padding: ${spacing[2]} ${spacing[1]};
`;

// 날짜 그리드 스타일
const DatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${spacing[1]};
`;

// 날짜 셀 스타일
const DateCell = styled.button<{ 
  isToday: boolean; 
  isSelected: boolean | null;
  isCurrentMonth: boolean;
  isInRange?: boolean;
  isDisabled?: boolean;
}>`
  background: ${props => {
    if (props.isSelected) return colors.primary[500];
    if (props.isInRange) return colors.primary[100];
    if (props.isToday) return colors.semantic.background.secondary;
    return 'transparent';
  }};
  color: ${props => {
    if (props.isSelected) return colors.semantic.background.primary;
    if (props.isDisabled) return colors.secondary[300];
    if (!props.isCurrentMonth) return colors.secondary[300];
    if (props.isToday) return colors.primary[600];
    return colors.semantic.text.primary;
  }};
  border: none;
  border-radius: ${borderRadius.md};
  padding: ${spacing[2]} ${spacing[1]};
  font-size: ${typography.fontSize.sm};
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.isToday ? typography.fontWeight.semibold : typography.fontWeight.normal};
  opacity: ${props => props.isDisabled ? 0.4 : 1};
  
  &:hover {
    background-color: ${props => {
      if (props.isDisabled) return 'transparent';
      if (props.isSelected) return colors.primary[600];
      return colors.semantic.background.secondary;
    }};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;

// Props 인터페이스
interface CalendarProps {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  placeholder?: string;
  maxDate?: Date; // 최대 선택 가능 날짜 (기본값: 오늘)
  /**
   * 날짜 이동 단위.
   * - day: 기존 동작(1일 단위)
   * - week: 1주(기본 7일) 단위 이동 + 입력 표시를 주 범위로 변경
   */
  mode?: 'day' | 'week';
  /** mode='week'일 때 1주 길이(기본 7) */
  stepDays?: number;
  /** week 모드에서 주 시작 요일(0=일요일, 1=월요일 ...) */
  weekStartsOn?: number;
}

// 요일 배열
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// 월 이름 배열
const MONTHS = [
  '1월', '2월', '3월', '4월', '5월', '6월',
  '7월', '8월', '9월', '10월', '11월', '12월'
];

// 캘린더 컴포넌트
const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateChange,
  placeholder = 'yy.mm.dd',
  maxDate = new Date(), // 기본값: 오늘 날짜
  mode = 'day',
  stepDays = 7,
  weekStartsOn = 1
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isWeekMode = mode === 'week';
  const unitDays = isWeekMode ? (stepDays || 7) : 1;

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const getWeekStart = useCallback((date: Date) => {
    // weekStartsOn: 0=일, 1=월 ...
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0..6 (일..토)
    const diff = (day - weekStartsOn + 7) % 7;
    d.setDate(d.getDate() - diff);
    return d;
  }, [weekStartsOn]);

  const getWeekEnd = (weekStart: Date) => addDays(weekStart, unitDays - 1);

  // week 모드일 때는 선택 기준일을 주 시작일로 정규화해서 사용합니다.
  const effectiveSelectedDate = selectedDate
    ? isWeekMode
      ? getWeekStart(selectedDate)
      : selectedDate
    : null;

  const monthBase = effectiveSelectedDate ?? new Date();
  const [currentMonth, setCurrentMonth] = useState(
    new Date(monthBase.getFullYear(), monthBase.getMonth())
  );
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 날짜를 표시 문자열로 포맷 (yy.mm.dd) — 일 단위 선택 표시용
  function formatDateToString(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const yy = String(year).slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${yy}.${month}.${day}`;
  }

  /** 주간 범위 표시용 yyyy.mm.dd */
  function formatDateToFullYearDot(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  }

  // 표시 문자열(yy.mm.dd 또는 yyyy-mm-dd)을 Date로 변환
  function parseDateString(dateString: string): Date | null {
    const trimmed = dateString.trim();
    let year: number;
    let month: number;
    let day: number;

    const y4 = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (y4) {
      year = Number(y4[1]);
      month = Number(y4[2]);
      day = Number(y4[3]);
    } else {
      const y2 = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
      if (!y2) return null;
      year = 2000 + Number(y2[1]);
      month = Number(y2[2]);
      day = Number(y2[3]);
    }

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
    return null;
  }

  // effectiveSelectedDate는 매 렌더마다 새 Date 참조가 될 수 있어, 동기화 effect 의존성으로 쓰면
  // 이전/다음 월 이동 직후 currentMonth가 매번 덮어써집니다. 실질 선택일(로컬 YYYY-MM-DD)만 키로 사용합니다.
  const selectedDateAnchorKey = useMemo(() => {
    if (!selectedDate) return '';
    const normalized = isWeekMode ? getWeekStart(selectedDate) : new Date(selectedDate);
    normalized.setHours(0, 0, 0, 0);
    return formatDateToString(normalized);
  }, [selectedDate, isWeekMode, getWeekStart]);

  // 입력 필드 클릭 핸들러
  const handleInputClick = () => {
    if (isWeekMode) return; // 주 범위 모드에서는 직접 입력을 허용하지 않습니다.
    setIsEditing(true);
    setInputValue(formatDateToString(effectiveSelectedDate));
    // 다음 렌더링 사이클에서 포커스
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  // 입력 필드 포커스 아웃 핸들러
  const handleInputBlur = () => {
    if (isWeekMode) return;
    setIsEditing(false);
    const parsedDate = parseDateString(inputValue);
    if (parsedDate) {
      onDateChange(parsedDate);
      setCurrentMonth(parsedDate);
    } else {
      // 유효하지 않은 날짜인 경우 원래 선택된 날짜로 복원
      setInputValue(formatDateToString(effectiveSelectedDate));
    }
  };

  // 입력 필드 키 다운 핸들러
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  // 날짜 이동 함수
  const changeDate = (days: number) => {
    const base = effectiveSelectedDate ?? new Date();
    const shifted = addDays(base, days);
    const normalized = isWeekMode ? getWeekStart(shifted) : shifted;

    // maxDate 이후로 이동하는 경우 방지
    if (maxDate && normalized > maxDate) {
      return;
    }

    onDateChange(normalized);
  };

  // 캘린더 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 부모에서 선택된 날짜(또는 주 모드 기준 주 시작일)가 바뀔 때만 표시 월을 맞춤
  useEffect(() => {
    if (!selectedDateAnchorKey) return;
    const parsed = parseDateString(selectedDateAnchorKey);
    if (!parsed) return;
    setCurrentMonth(new Date(parsed.getFullYear(), parsed.getMonth()));
  }, [selectedDateAnchorKey]);

  // 이전 달로 이동
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  // 다음 달로 이동
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // 날짜 선택 처리
  const handleDateSelect = (date: Date) => {
    // maxDate 이후의 날짜는 선택할 수 없음
    if (maxDate && date > maxDate) {
      return;
    }
    const normalized = isWeekMode ? getWeekStart(date) : date;
    onDateChange(normalized);
    setIsOpen(false);
  };

  // 캘린더 날짜 생성
  const generateCalendarDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // 해당 월의 첫 번째 날과 마지막 날
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 첫 번째 주의 시작일 (일요일부터 시작)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // 마지막 주의 종료일
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const calendarDates = generateCalendarDates();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 이전/다음 버튼 비활성화 상태 계산
  const unitPrev = effectiveSelectedDate ? addDays(effectiveSelectedDate, -unitDays) : null;
  const unitNext = effectiveSelectedDate ? addDays(effectiveSelectedDate, unitDays) : null;

  const isPrevDisabled = maxDate && unitPrev ? unitPrev > maxDate : false;
  const isNextDisabled = maxDate && unitNext ? unitNext > maxDate : false;

  const weekRangeTitle =
    isWeekMode && effectiveSelectedDate
      ? `${formatDateToFullYearDot(effectiveSelectedDate)} ~ ${formatDateToFullYearDot(getWeekEnd(effectiveSelectedDate))}`
      : undefined;

  return (
    <CalendarContainer ref={calendarRef}>
      {/* 이전 날짜 버튼 */}
      <IconButton 
        onClick={() => changeDate(-unitDays)} 
        title={isWeekMode ? '이전 주' : '이전 날짜'}
        disabled={isPrevDisabled}
        style={{ opacity: isPrevDisabled ? 0.4 : 1, cursor: isPrevDisabled ? 'not-allowed' : 'pointer' }}
      >
        <FiChevronLeft size={18} />
      </IconButton>

      {/* 날짜 입력 필드 */}
      <DateInput
        ref={inputRef}
        type="text"
        readOnly={isWeekMode}
        $weekMode={isWeekMode}
        title={weekRangeTitle}
        value={
          isWeekMode
            ? effectiveSelectedDate
              ? `${formatDateToFullYearDot(effectiveSelectedDate)} ~ ${formatDateToFullYearDot(getWeekEnd(effectiveSelectedDate))}`
              : ''
            : isEditing
              ? inputValue
              : formatDateToString(effectiveSelectedDate)
        }
        placeholder={placeholder}
        onClick={handleInputClick}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        className={isEditing ? 'editing' : ''}
      />

      {/* 오늘로 이동 */}
      <TodayButton
        type="button"
        onClick={() => {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const normalized = isWeekMode ? getWeekStart(now) : now;
          if (maxDate && normalized > maxDate) return;
          onDateChange(normalized);
          setIsOpen(false);
          setIsEditing(false);
        }}
        title="오늘"
        style={{ opacity: 1 }}
      >
        <FiRefreshCw size={16} />
      </TodayButton>

      {/* 캘린더 토글 버튼 */}
      <IconButton onClick={() => setIsOpen(!isOpen)} title="캘린더 열기/닫기">
        <FiCalendar size={18} />
      </IconButton>

      {/* 다음 날짜 버튼 */}
      <IconButton 
        onClick={() => changeDate(unitDays)} 
        title={isWeekMode ? '다음 주' : '다음 날짜'}
        disabled={isNextDisabled}
        style={{ opacity: isNextDisabled ? 0.4 : 1, cursor: isNextDisabled ? 'not-allowed' : 'pointer' }}
      >
        <FiChevronRight size={18} />
      </IconButton>
      
      {/* 캘린더 팝업 */}
      <CalendarPopup isOpen={isOpen}>
        {/* 캘린더 헤더 */}
        <CalendarHeader>
          <NavButton onClick={goToPreviousMonth}>‹</NavButton>
          <MonthYear>
            {currentMonth.getFullYear()}년 {MONTHS[currentMonth.getMonth()]}
          </MonthYear>
          <NavButton onClick={goToNextMonth}>›</NavButton>
        </CalendarHeader>
        
        {/* 요일 헤더 */}
        <WeekdaysHeader>
          {WEEKDAYS.map(weekday => (
            <WeekdayCell key={weekday}>{weekday}</WeekdayCell>
          ))}
        </WeekdaysHeader>
        
        {/* 날짜 그리드 */}
        <DatesGrid>
          {calendarDates.map((date, index) => {
            const isToday = date.getTime() === today.getTime();
            const isSelected = effectiveSelectedDate && date.getTime() === effectiveSelectedDate.getTime();
            const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
            const isDisabled = maxDate && date > maxDate;
            
            return (
              <DateCell
                key={index}
                isToday={isToday}
                isSelected={isSelected}
                isCurrentMonth={isCurrentMonth}
                isDisabled={isDisabled}
                onClick={() => handleDateSelect(date)}
                disabled={isDisabled}
              >
                {date.getDate()}
              </DateCell>
            );
          })}
        </DatesGrid>
      </CalendarPopup>
    </CalendarContainer>
  );
};

export default Calendar; 